/**
 * Debug Logger Utility for WordGame
 * Logs debug information to both console and a downloadable file
 */

class DebugLogger {
  constructor() {
    this.logs = [];
    this.isEnabled = true; // Set to false to disable logging
    this.maxLogs = 1000; // Prevent memory issues
    this.lastLogHash = null; // Track last log to prevent duplicates
  }

  log(category, message, data = {}) {
    if (!this.isEnabled) return;

    const timestamp = new Date().toISOString();
    const logEntry = {
      timestamp,
      category,
      message,
      data: JSON.parse(JSON.stringify(data)) // Deep clone to avoid reference issues
    };

    // Create a hash of the log content to detect duplicates
    const logHash = JSON.stringify({ category, message, data });

    // Skip if this is identical to the last log (prevents rapid duplicates)
    if (this.lastLogHash === logHash) {
      return;
    }

    this.lastLogHash = logHash;

    // Add to internal log storage
    this.logs.push(logEntry);

    // Trim logs if too many
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }

    // Also log to console with emoji for easy identification
    const emoji = this.getCategoryEmoji(category);
    console.log(`${emoji} ${message}`, data);
  }

  getCategoryEmoji(category) {
    const emojiMap = {
      'word-submission': '🎯',
      'state-change': '📊',
      'bug-fix': '🔧',
      'error': '❌',
      'info': 'ℹ️'
    };
    return emojiMap[category] || '📝';
  }

  downloadLogs(gameStats = null) {
    const logData = {
      generatedAt: new Date().toISOString(),
      gameSession: 'WordGame Debug',
      totalLogs: this.logs.length,
      gameStatistics: gameStats,
      logs: this.logs
    };

    const jsonString = JSON.stringify(logData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const filename = gameStats ?
      `wordgame-debug-game-${gameStats.finalScore}pts-${timestamp}.json` :
      `wordgame-debug-${Date.now()}.json`;

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    console.log('📁 Debug logs downloaded successfully!', gameStats ? `Game stats included: ${gameStats.finalScore} points` : '');
  }

  clearLogs() {
    this.logs = [];
    this.lastLogHash = null; // Reset duplicate detection
    console.log('🗑️ Debug logs cleared');
  }

  getLogSummary() {
    const summary = {
      totalLogs: this.logs.length,
      categories: {},
      timeRange: {
        start: this.logs[0]?.timestamp,
        end: this.logs[this.logs.length - 1]?.timestamp
      }
    };

    this.logs.forEach(log => {
      summary.categories[log.category] = (summary.categories[log.category] || 0) + 1;
    });

    return summary;
  }

  // Convenience methods for specific categories
  logWordSubmission(message, data) {
    this.log('word-submission', message, data);
  }

  logStateChange(message, data) {
    this.log('state-change', message, data);
  }

  logBugFix(message, data) {
    this.log('bug-fix', message, data);
  }

  logError(message, data) {
    this.log('error', message, data);
  }

  logInfo(message, data) {
    this.log('info', message, data);
  }
}

// Create singleton instance
const debugLogger = new DebugLogger();

// Add global access for easy debugging in browser console
if (typeof window !== 'undefined') {
  window.debugLogger = debugLogger;
  window.downloadDebugLogs = () => debugLogger.downloadLogs();
  console.log('🔧 Debug Logger initialized. Use window.downloadDebugLogs() to save logs to file.');
}

export default debugLogger;
